import { Color } from "@repo/app-types/constants/colors";
import type {
  AdminApiPlaybook,
  PlaybookCriterionNode,
  PlaybookNode,
  PlaybookSectionNode,
} from "@repo/app-types/entities/playbook";
import type {
  RecordingGradingAssessment,
  RecordingId,
} from "@repo/app-types/entities/recording";
import type { RecordingAssessmentFeedback } from "@repo/app-types/entities/recordingAssessmentFeedback";
import type { PlaybookTestSpecExpected } from "@repo/app-types/entities/teammateAgent";
import { findMatchingSectionFeedback } from "@repo/app-types/helpers/recordingAssessmentFeedbackHelpers";
import { Size } from "@repo/ui/helpers/helpers";
import { Pill } from "@repo/ui/Pills/pill";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import {
  IconCircleCheck,
  IconCircleX,
  IconExclamationCircle,
} from "@tabler/icons-react";
import { type FC, type ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";
import {
  AssessmentCriteriaNode,
  AssessmentCriteriaNodeCommentary,
  AssessmentCriteriaNodeGrading,
  AssessmentCriteriaNodeTitle,
} from "../../components/assessment/assessmentCriteriaNode.js";
import {
  AssessmentSectionNode,
  AssessmentSectionNodeContent,
  AssessmentSectionNodeGrading,
  AssessmentSectionNodeTitle,
} from "../../components/assessment/assessmentSectionNode.js";
import { AssessmentTable } from "../../components/assessment/assessmentTable.js";
import { getAccuracyClassificationForGradingValue } from "../../components/recordingAssessmentEditor/feedbackAccuracyHelpers.js";
import { useModalNavigation } from "../../hooks/useModalNavigation.js";
import { GradingAssessmentFeedbackButton } from "./gradingAssessmentFeedbackButton.js";
import { GradingAssessmentSectionSummary } from "./gradingAssessmentSectionSummary.js";
import { renderGradingValue } from "./gradingHelpers.js";

/**
 * Recursively collects section IDs that are not marked as "not applicable"
 */
// TODO: Move this function somewhere else, probably app-types.
function getRelevantSectionIds(
  nodes: readonly PlaybookNode[],
  assessment: RecordingGradingAssessment,
): string[] {
  const relevantIds: string[] = [];

  for (const node of nodes) {
    if (node.type === "section") {
      const sectionGrading = assessment.section_grading?.[node.id];
      const isNotApplicable = sectionGrading?.is_not_applicable;

      // Include section if it's not marked as "not applicable"
      if (!isNotApplicable) {
        relevantIds.push(node.id);
      }

      // Recursively check child nodes
      relevantIds.push(...getRelevantSectionIds(node.nodes, assessment));
    }
  }

  return relevantIds;
}

interface GradingAssessmentTableProps {
  assessment: RecordingGradingAssessment;
  recordingId: RecordingId;
  playbook: AdminApiPlaybook;
  feedback: readonly RecordingAssessmentFeedback[] | undefined;
  disableNodeClicks?: boolean;
  disableFeedback?: boolean;
  expectedValues?: PlaybookTestSpecExpected["nodes"];
}

export const GradingAssessmentTable: FC<GradingAssessmentTableProps> = (
  props,
) => {
  const modalNavigation = useModalNavigation();
  const {
    assessment,
    recordingId,
    playbook,
    feedback,
    disableNodeClicks,
    disableFeedback,
    expectedValues,
  } = props;

  // Collect IDs of sections that are not marked as "not applicable"
  const relevantSectionIds = getRelevantSectionIds(
    playbook.assessment_tree.nodes,
    assessment,
  );

  return (
    <AssessmentTable defaultExpandedIds={relevantSectionIds}>
      {renderNodes(
        recordingId,
        playbook.assessment_tree.nodes,
        assessment,
        feedback,
        disableNodeClicks
          ? undefined
          : (node) =>
              modalNavigation.push(`criteria/${encodeURIComponent(node.id)}`),
        disableFeedback,
        expectedValues,
      )}
    </AssessmentTable>
  );
};

function renderNodes(
  recordingId: RecordingId,
  nodes: readonly PlaybookNode[],
  assessment: RecordingGradingAssessment,
  feedback: readonly RecordingAssessmentFeedback[] | undefined,
  onOpenValidationDialog: ((node: PlaybookCriterionNode) => void) | undefined,
  disableFeedback?: boolean,
  expectedValues?: PlaybookTestSpecExpected["nodes"],
) {
  return nodes.map((node) =>
    renderNode(
      recordingId,
      node,
      assessment,
      feedback,
      onOpenValidationDialog,
      disableFeedback,
      expectedValues,
    ),
  );
}

function renderNode(
  recordingId: RecordingId,
  node: PlaybookNode,
  assessment: RecordingGradingAssessment,
  feedback: readonly RecordingAssessmentFeedback[] | undefined,
  onOpenValidationDialog: ((node: PlaybookCriterionNode) => void) | undefined,
  disableFeedback?: boolean,
  expectedValues?: PlaybookTestSpecExpected["nodes"],
) {
  switch (node.type) {
    case "section":
      return (
        <Section
          key={node.id}
          recordingId={recordingId}
          node={node}
          assessment={assessment}
          feedback={feedback}
          onOpenValidationDialog={onOpenValidationDialog}
          disableFeedback={disableFeedback}
          expectedValues={expectedValues}
        />
      );

    case "criteria":
      return (
        <Criteria
          key={node.id}
          node={node}
          assessment={assessment}
          onOpenValidationDialog={onOpenValidationDialog}
          expectedValues={expectedValues}
        />
      );
  }
}

//
// Section tree item.
//

interface SectionProps {
  recordingId: RecordingId;
  node: PlaybookSectionNode;
  assessment: RecordingGradingAssessment;
  feedback: readonly RecordingAssessmentFeedback[] | undefined;
  onOpenValidationDialog: ((node: PlaybookCriterionNode) => void) | undefined;
  disableFeedback?: boolean;
  expectedValues?: PlaybookTestSpecExpected["nodes"];
}

const Section: FC<SectionProps> = (props) => {
  const {
    recordingId,
    node,
    assessment,
    feedback: feedbackList,
    disableFeedback,
    expectedValues,
  } = props;
  const sectionGrading = assessment.section_grading?.[node.id];
  const isNotApplicable = sectionGrading?.is_not_applicable;
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);

  const sectionFeedback = findMatchingSectionFeedback(feedbackList, node.id);

  const grade = {
    value: isNotApplicable ? "n/a" : "a",
    commentary: sectionGrading?.commentary || "",
  };
  const gradingSummary = isNotApplicable ? (
    <Pill size={Size.SMALL} color={Color.Gray} label="N/A" />
  ) : (
    <GradingAssessmentSectionSummary section={node} assessment={assessment} />
  );

  // Calculate accuracy status for section N/A marking
  const expectedValue = expectedValues?.[node.id];
  const accuracyStatus = getAccuracyClassificationForGradingValue(
    "section",
    isNotApplicable ? "N/A" : "applicable",
    String(expectedValue),
  );

  return (
    <AssessmentSectionNode id={node.id} textValue={node.name}>
      <AssessmentSectionNodeContent>
        <AssessmentSectionNodeTitle isNotApplicable={isNotApplicable}>
          <div className="flex items-center gap-2">
            {maybeRenderAccuracyIndicator(
              accuracyStatus,
              expectedValue,
              node.id,
            )}
            {node.name}
          </div>
        </AssessmentSectionNodeTitle>
        <AssessmentSectionNodeGrading>
          {!disableFeedback && (
            <div
              className={twMerge(
                "invisible flex group-hover:visible",
                (isFeedbackDialogOpen || sectionFeedback) && "visible",
              )}
            >
              <GradingAssessmentFeedbackButton
                playbookId={assessment.recording_assessment_template_id}
                node={node}
                recordingId={recordingId}
                grade={grade}
                feedback={sectionFeedback}
                onOpenChange={setIsFeedbackDialogOpen}
              ></GradingAssessmentFeedbackButton>
            </div>
          )}
          {sectionGrading?.commentary ? (
            <TooltipTrigger>
              <TooltipHoverTarget shape="rounded">
                {gradingSummary}
              </TooltipHoverTarget>
              <SimpleTooltip>{sectionGrading.commentary}</SimpleTooltip>
            </TooltipTrigger>
          ) : (
            gradingSummary
          )}
        </AssessmentSectionNodeGrading>
      </AssessmentSectionNodeContent>
      {renderNodes(
        recordingId,
        node.nodes,
        assessment,
        feedbackList,
        props.onOpenValidationDialog,
        disableFeedback,
        props.expectedValues,
      )}
    </AssessmentSectionNode>
  );
};

//
// Criteria tree item.
//

interface CriteriaProps {
  node: PlaybookCriterionNode;
  assessment: RecordingGradingAssessment;
  onOpenValidationDialog: ((node: PlaybookCriterionNode) => void) | undefined;
  expectedValues?: PlaybookTestSpecExpected["nodes"];
}

/**
 * Renders an accuracy indicator icon with tooltip based on status - used for showing test case accuracy
 */
function maybeRenderAccuracyIndicator(
  status: "correct" | "close" | "incorrect" | undefined,
  expectedValue: PlaybookTestSpecExpected["nodes"][string] | undefined,
  nodeId: string,
) {
  if (!status || !expectedValue) return null;

  let icon: ReactNode;
  let tooltipContent: string;

  switch (status) {
    case "correct":
      icon = <IconCircleCheck className="size-5 text-success" />;
      tooltipContent = `Matches expected value of "${expectedValue}" from test case`;
      break;
    case "close":
      icon = <IconExclamationCircle className="size-5 text-yellow-600" />;
      tooltipContent = `Close to expected value of "${expectedValue}" from test case`;
      break;
    case "incorrect":
      icon = <IconCircleX className="size-5 text-destructive" />;
      tooltipContent = `Does not match expected value of "${expectedValue}" from test case`;
      break;
  }

  return (
    <TooltipTrigger>
      <TooltipHoverTarget shape="rounded">{icon}</TooltipHoverTarget>
      <SimpleTooltip>{tooltipContent}</SimpleTooltip>
    </TooltipTrigger>
  );
}

const Criteria: FC<CriteriaProps> = (props) => {
  const { node, assessment, expectedValues } = props;
  const grading = assessment.criteria_grading[node.id];
  const expectedValue = expectedValues?.[node.id];
  const accuracyStatus = getAccuracyClassificationForGradingValue(
    node.grading_value_type,
    grading?.value,
    String(expectedValue),
  );

  return (
    <AssessmentCriteriaNode
      textValue={node.name}
      onPress={
        grading && props.onOpenValidationDialog
          ? () => props.onOpenValidationDialog!(node)
          : undefined
      }
    >
      <AssessmentCriteriaNodeTitle>{node.name}</AssessmentCriteriaNodeTitle>

      <AssessmentCriteriaNodeGrading>
        <div className="flex items-center gap-2">
          {renderGradingValue(node.grading_value_type, grading?.value)}
          {maybeRenderAccuracyIndicator(accuracyStatus, expectedValue, node.id)}
        </div>
      </AssessmentCriteriaNodeGrading>

      <AssessmentCriteriaNodeCommentary>
        {grading?.commentary}
      </AssessmentCriteriaNodeCommentary>
    </AssessmentCriteriaNode>
  );
};
